import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { onAuthChange } from '@shared/services/auth';
import { useAuthStore, useFeatureFlagStore } from '@shared/state';
import { AppLayout } from './components/ui/AppLayout';
import { AnalyticsTracker } from './components/AnalyticsTracker';
import { AchievementToast } from './components/gamification/AchievementToast';
import { FeedbackButton } from './components/feedback/FeedbackButton';
import { FeedbackPanel } from './components/feedback/FeedbackPanel';
import { SystemDiagnosticRunner } from './components/system/SystemDiagnosticRunner';
import { ToastProvider } from './components/ui/Toast';
import { PublicLayout } from './components/PublicLayout';
import { ScrollRestorer, ScrollToTopButton } from './components/ui/ScrollToTop';
import { Landing } from './pages/Landing';
import { HowItWorksPage } from './pages/HowItWorksPage';
import { PricingPage } from './pages/PricingPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { TermsPage } from './pages/TermsPage';
import { TrustPage } from './pages/TrustPage';
import { ForTeamsPage } from './pages/ForTeamsPage';
import { TeamPage } from './pages/TeamPage';
import { Login } from './pages/Login';
import { Signup } from './pages/Signup';
import { Dashboard } from './pages/Dashboard';
import { Profile } from './pages/Profile';
import { DiagnosticPage } from './pages/DiagnosticPage';
import { ProfileRevealPage } from './pages/ProfileRevealPage';
import { UploadPage } from './pages/UploadPage';
import { LearningPage } from './pages/LearningPage';
import { CheckoutSuccessPage } from './pages/CheckoutSuccessPage';
import { ProfileEvolutionPage } from './pages/ProfileEvolutionPage';
import { ProfileProgressPage } from './pages/ProfileProgressPage';
import { LearningInsightsPage } from './pages/LearningInsightsPage';
import { AdaptationHistoryPage } from './pages/AdaptationHistoryPage';
import { MonthlyReportPage } from './pages/MonthlyReportPage';
import { AdminContentLabWrapper } from './pages/admin/AdminContentLabWrapper';
import { AdminLogin } from './pages/admin/AdminLogin';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { AdminUsers } from './pages/admin/AdminUsers';
import { AdminWaitlist } from './pages/admin/AdminWaitlist';
import { AdminAnalytics } from './pages/admin/AdminAnalytics';
import { AdminFeatureFlags } from './pages/admin/AdminFeatureFlags';
import { AdminSettings } from './pages/admin/AdminSettings';
import { AdminRoute } from './components/admin/AdminRoute';
import { ComingSoonPage } from './pages/ComingSoonPage';

// Holding-page mode. The same bundle is served on every domain pointed at
// this Firebase site (cogniprint.io, the *.web.app fallback, *.firebaseapp.com,
// localhost), so the gate is hostname-based — only cogniprint.io shows the
// holding page. Set VITE_COMING_SOON=force locally to preview it on any host.
const COMING_SOON_HOSTS = new Set(['cogniprint.io', 'www.cogniprint.io']);
const COMING_SOON =
  import.meta.env.VITE_COMING_SOON === 'force' ||
  (typeof window !== 'undefined' && COMING_SOON_HOSTS.has(window.location.hostname));

export function App() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const setLoading = useAuthStore((s) => s.setLoading);

  useEffect(() => {
    setLoading(true);
    const unsubscribe = onAuthChange((user) => {
      setUser(user);
    });
    return unsubscribe;
  }, [setUser, setLoading]);

  // Initialize Lemon.js overlay
  useEffect(() => {
    window.createLemonSqueezy?.();
  }, []);

  // Subscribe to global feature flags (recalibration v2 surfaces).
  useEffect(() => useFeatureFlagStore.getState().listen(), []);

  return (
    <ToastProvider>
    <ScrollRestorer />
    <ScrollToTopButton />
    <AnalyticsTracker />
    <SystemDiagnosticRunner />
    <FeedbackButton />
    <FeedbackPanel />
    {user && <AchievementToast userId={user.uid} />}
    <Routes>
      {/* Landing has its own nav. When VITE_COMING_SOON=1, "/" shows the
         holding page instead. */}
      <Route path="/" element={COMING_SOON ? <ComingSoonPage /> : <Landing />} />

      {/* Public marketing pages — gated behind COMING_SOON. When the flag is
         on, every public route redirects to "/" so casual visitors only ever
         see the holding page. */}
      {COMING_SOON ? (
        <>
          <Route path="/how-it-works" element={<Navigate to="/" replace />} />
          <Route path="/pricing" element={<Navigate to="/" replace />} />
          <Route path="/privacy" element={<Navigate to="/" replace />} />
          <Route path="/terms" element={<Navigate to="/" replace />} />
          <Route path="/trust" element={<Navigate to="/" replace />} />
          <Route path="/for-teams" element={<Navigate to="/" replace />} />
        </>
      ) : (
        <Route element={<PublicLayout />}>
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/trust" element={<TrustPage />} />
          <Route path="/for-teams" element={<ForTeamsPage />} />
        </Route>
      )}

      {/* Auth / App pages — all wrapped in AppLayout so the shared Header renders.
          Header is auth-aware: PublicHeader for /login & /signup, AuthenticatedHeader otherwise. */}
      <Route element={<AppLayout />}>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/profile/reveal" element={<ProfileRevealPage />} />
        <Route path="/profile/evolution" element={<ProfileEvolutionPage />} />
        <Route path="/profile/progress" element={<ProfileProgressPage />} />
        <Route path="/profile/insights" element={<LearningInsightsPage />} />
        <Route path="/profile/adaptations" element={<AdaptationHistoryPage />} />
        <Route path="/diagnostic" element={<DiagnosticPage />} />
        <Route path="/upload" element={<UploadPage />} />
        <Route path="/learn/:contentId" element={<LearningPage />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/monthly-report" element={<MonthlyReportPage />} />
        <Route path="/pricing/success" element={<CheckoutSuccessPage />} />
      </Route>

      {/* Admin — separate login, own layout (sidebar rendered per-page) */}
      <Route path="/admin-login" element={<AdminLogin />} />
      <Route path="/admin-dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/admin-dashboard/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
      <Route path="/admin-dashboard/waitlist" element={<AdminRoute><AdminWaitlist /></AdminRoute>} />
      <Route path="/admin-dashboard/content-lab" element={<AdminRoute><AdminContentLabWrapper /></AdminRoute>} />
      <Route path="/admin-dashboard/analytics" element={<AdminRoute><AdminAnalytics /></AdminRoute>} />
      <Route path="/admin-dashboard/feature-flags" element={<AdminRoute><AdminFeatureFlags /></AdminRoute>} />
      <Route path="/admin-dashboard/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
    </Routes>
    </ToastProvider>
  );
}
